import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import { getDefaultCodeBlockAttrs } from '@atlaskit/editor-common/code-block';
import { insertBlock } from '@atlaskit/editor-common/commands/insert-block';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { InputRuleWrapper } from '@atlaskit/editor-common/types/input-rules';
import { createRule } from '@atlaskit/editor-common/utils';
import { inputRuleWithAnalytics } from '@atlaskit/editor-common/utils/input-rules';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';
import { leafNodeReplacementCharacter } from '@atlaskit/prosemirror-input-rules/constants';
import { createPlugin } from '@atlaskit/prosemirror-input-rules/utils';

import { isConvertableToCodeBlock, transformToCodeBlockAction } from './transform-to-code-block';

// eslint-disable-next-line require-unicode-regexp
const THREE_TILDE_RULE_REGEX = /(?!\s)(`{3,})$/;

export function createCodeBlockInputRule(
	schema: Schema,
	editorAnalyticsAPI?: EditorAnalyticsAPI,
): SafePlugin {
	const rules: Array<InputRuleWrapper> = getCodeBlockRules(editorAnalyticsAPI, schema);
	return new SafePlugin(
		createPlugin('code-block-input-rule', rules, {
			isBlockNodeRule: true,
		}),
	);
}

/**
 * Get all code block input rules
 *
 * @param {Schema} schema
 * @returns {InputRuleWithHandler[]}
 */
function getCodeBlockRules(
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	schema: Schema,
): InputRuleWrapper[] {
	const ruleAnalytics = inputRuleWithAnalytics(
		{
			action: ACTION.INSERTED,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			actionSubjectId: ACTION_SUBJECT_ID.CODE_BLOCK,
			attributes: { inputMethod: INPUT_METHOD.FORMATTING },
			eventType: EVENT_TYPE.TRACK,
		},
		editorAnalyticsAPI,
	);

	const validMatchLength = (match: RegExpExecArray) => match.length > 0 && match[0].length === 3;

	const threeTildeRule = createRule(THREE_TILDE_RULE_REGEX, (state, match, start, end) => {
		if (!validMatchLength(match)) {
			return null;
		}

		const attributes = getDefaultCodeBlockAttrs(match[4] ? { language: match[4] } : {});

		if (isConvertableToCodeBlock(state)) {
			return transformToCodeBlockAction(state, start, attributes);
		}

		const tr = state.tr;
		tr.delete(start, end);
		const codeBlock = tr.doc.type.schema.nodes.codeBlock.createChecked(attributes);
		safeInsert(codeBlock)(tr);

		return tr;
	});

	const leftNodeReplacementThreeTildeRule = createRule(
		// Ignored via go/ees005
		// eslint-disable-next-line require-unicode-regexp
		new RegExp(`((${leafNodeReplacementCharacter}\`{3,})|^\\s(\`{3,}))(\\S*)$`),
		(state, match, start, end) => {
			if (!validMatchLength(match)) {
				return null;
			}

			const attributes = getDefaultCodeBlockAttrs(match[4] ? { language: match[4] } : {});
			const inlineStart = Math.max(match.index + state.selection.$from.start(), 1);
			return insertBlock(state, schema.nodes.codeBlock, inlineStart, end, attributes);
		},
	);

	return [ruleAnalytics(threeTildeRule), ruleAnalytics(leftNodeReplacementThreeTildeRule)];
}
